/**
 * RecordingConsentModal Component
 * 
 * Displays consent notice to patients before joining a recorded telehealth call.
 * HIPAA-compliant consent tracking for recordings and transcription.
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Video, 
  FileText, 
  ShieldCheck, 
  X,
  CheckCircle2,
  Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface RecordingConsentModalProps {
  open: boolean
  onConsent: () => void
  onDecline: () => void
  clinicName?: string
  providerName?: string
  transcriptionEnabled?: boolean
}

const CONSENT_STORAGE_KEY = 'novamed_recording_consent'

const RecordingConsentModal: React.FC<RecordingConsentModalProps> = ({
  open,
  onConsent,
  onDecline,
  clinicName = 'Nimbbus Providers',
  providerName,
  transcriptionEnabled = true
}) => {
  const [hasAgreed, setHasAgreed] = useState(false)
  const [hasAcknowledgedHIPAA, setHasAcknowledgedHIPAA] = useState(false)

  const canProceed = hasAgreed && hasAcknowledgedHIPAA

  const handleConsent = () => {
    const consentRecord = {
      consented: true,
      timestamp: new Date().toISOString(),
      transcription: transcriptionEnabled
    }
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentRecord))
    onConsent()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDecline()}>
      <DialogContent 
        className="max-w-[460px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden border border-slate-700/50 bg-[#0f1419] rounded-2xl shadow-2xl shadow-black/60 [&>button]:hidden"
      >
        {/* Header */}
        <div className="relative px-5 pt-5 pb-4">
          {/* Top accent line */}
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-brand-teal/60 to-transparent" />
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-[#1a2530] flex items-center justify-center border border-brand-teal/30">
                  <ShieldCheck className="h-5 w-5 text-brand-teal" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#1a2530] border border-brand-teal/40 flex items-center justify-center">
                  <Video className="h-2 w-2 text-brand-teal" />
                </div>
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-white">
                  Recording & Transcription Notice
                </h2>
                <p className="text-slate-400 text-xs mt-0.5">
                  Please review before joining your video visit
                </p>
              </div>
            </div>
            <button 
              onClick={onDecline}
              className="w-8 h-8 rounded-full bg-[#1a2530] hover:bg-[#243040] flex items-center justify-center transition-colors border border-slate-700/50"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-3 min-h-0">
          {/* Recording Notice */}
          <div className="p-4 rounded-xl bg-[#141c24] border border-slate-700/40">
            <div className="flex gap-3.5">
              <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0 border border-rose-500/20">
                <Video className="h-4 w-4 text-rose-400" />
              </div>
              <div className="min-w-0 pt-0.5">
                <h4 className="text-white font-medium text-[13px] mb-1">
                  This Call May Be Recorded
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Your telehealth visit with <span className="text-slate-300">{providerName || 'your provider'}</span> may be recorded 
                  for quality assurance and to assist with clinical documentation.
                </p>
              </div>
            </div>
          </div>

          {/* Transcription Notice */}
          {transcriptionEnabled && (
            <div className="p-4 rounded-xl bg-[#141c24] border border-slate-700/40">
              <div className="flex gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                  <FileText className="h-4 w-4 text-blue-400" />
                </div>
                <div className="min-w-0 pt-0.5">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-medium text-[13px]">
                      AI Transcription Enabled
                    </h4>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 text-[10px] font-medium border border-blue-500/20">
                      <Sparkles className="h-2.5 w-2.5" />
                      AI
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    This call uses AI-powered transcription to help your provider create 
                    accurate clinical notes. The transcript becomes part of your medical record.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* HIPAA Notice */}
          <div className="p-4 rounded-xl bg-[#141c24] border border-brand-teal/20">
            <div className="flex gap-3.5">
              <div className="w-9 h-9 rounded-lg bg-brand-teal/10 flex items-center justify-center flex-shrink-0 border border-brand-teal/25">
                <ShieldCheck className="h-4 w-4 text-brand-teal" />
              </div>
              <div className="min-w-0 pt-0.5">
                <h4 className="text-white font-medium text-[13px] mb-1">
                  HIPAA Protected
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  All recordings and transcripts are encrypted and stored securely in 
                  compliance with HIPAA regulations. Only authorized healthcare providers 
                  at <span className="text-slate-300">{clinicName}</span> can access this information.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Consent Checkboxes */}
        <div className="px-5 py-4 space-y-3 border-t border-slate-700/40 bg-[#0c1015]">
          <label className="flex items-start gap-3 cursor-pointer group">
            <Checkbox
              id="consent-recording"
              checked={hasAgreed}
              onCheckedChange={(checked) => setHasAgreed(checked as boolean)}
              className={cn(
                "mt-0.5 border-2 rounded transition-all duration-200 h-4 w-4",
                hasAgreed 
                  ? "bg-brand-teal border-brand-teal data-[state=checked]:bg-brand-teal data-[state=checked]:border-brand-teal" 
                  : "border-slate-600 bg-transparent hover:border-slate-500"
              )}
            />
            <span className="text-xs text-slate-300 leading-relaxed">
              I consent to the recording{transcriptionEnabled ? ' and AI transcription' : ''} of 
              this telehealth visit for clinical documentation purposes.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <Checkbox
              id="consent-hipaa"
              checked={hasAcknowledgedHIPAA}
              onCheckedChange={(checked) => setHasAcknowledgedHIPAA(checked as boolean)}
              className={cn(
                "mt-0.5 border-2 rounded transition-all duration-200 h-4 w-4",
                hasAcknowledgedHIPAA 
                  ? "bg-brand-teal border-brand-teal data-[state=checked]:bg-brand-teal data-[state=checked]:border-brand-teal" 
                  : "border-slate-600 bg-transparent hover:border-slate-500"
              )}
            />
            <span className="text-xs text-slate-300 leading-relaxed">
              I understand that my information will be handled in accordance with HIPAA 
              privacy regulations and {clinicName}'s privacy policy.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-[#0c1015] border-t border-slate-700/40 flex gap-3">
          <Button
            variant="outline"
            onClick={onDecline}
            className="flex-1 h-10 bg-transparent border-slate-600/60 text-slate-300 hover:bg-[#1a2530] hover:text-white hover:border-slate-500 text-sm font-medium"
          >
            Decline & Leave
          </Button>
          <Button
            onClick={handleConsent}
            disabled={!canProceed}
            className={cn(
              "flex-1 h-10 font-medium text-sm transition-all duration-200",
              canProceed
                ? "bg-brand-teal hover:bg-brand-teal/90 text-white"
                : "bg-slate-700/50 text-slate-500 cursor-not-allowed border-0"
            )}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            I Agree — Join Call
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Check if user has previously consented to recording
 */
export const hasRecordingConsent = (): boolean => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!stored) return false
    
    const consent = JSON.parse(stored)
    const consentTime = new Date(consent.timestamp).getTime()
    const now = Date.now()
    const hoursSinceConsent = (now - consentTime) / (1000 * 60 * 60)
    
    return consent.consented && hoursSinceConsent < 24
  } catch {
    return false
  }
}

/**
 * Clear stored consent
 */
export const clearRecordingConsent = (): void => {
  localStorage.removeItem(CONSENT_STORAGE_KEY)
}

export default RecordingConsentModal
